import { keepPreviousData } from '@tanstack/react-query';
import {
  type ColumnDef,
  type PaginationState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Building2, MoreHorizontal } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import type { ListVendors } from '@cold-ambulance/client';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@cold-ambulance/shadcn';
import {
  ConfirmDialog,
  SearchInput,
  TableRowSort,
  useAction,
  useData,
} from '@cold-ambulance/ui';

import {
  createdAtColumn,
  emailColumn,
  nameColumn,
  phoneNumberColumn,
} from '../../components/columns.tsx';
import { CountablePageHeader } from '../../components/page-header.tsx';
import { Pagination } from '../../components/pagination.tsx';
import { RejectApplicantDialog } from '../../components/reject-applicant-dialog.tsx';
import {
  StickyTableBox,
  StickyTableHeader,
} from '../../components/sticky-table-box.tsx';
import { UpdateVendorDialog } from './update-vendor.tsx';
import { ViewVendorDocumentsDialog } from './view-vendor-documents-dialog.tsx';

type VendorRecord = ListVendors['records'][number];

const columns: ColumnDef<VendorRecord>[] = [
  nameColumn(),
  emailColumn(),
  phoneNumberColumn((row) => row.contact),
  {
    accessorKey: 'approved',
    header: 'Status',
    cell: ({ row }) => {
      const approved = row.original.approved;
      if (approved === true) {
        return <Badge variant="default">Approved</Badge>;
      }
      if (approved === false) {
        return <Badge variant="destructive">Rejected</Badge>;
      }
      return <Badge variant="secondary">Pending</Badge>;
    },
  },
  createdAtColumn(),
  {
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => <VendorActionsCell vendor={row.original} />,
  },
];

export default function VendorsPage() {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchQuery, setSearchQuery] = useState<string | undefined>('');
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const dataQuery = useData(
    'GET /vendors',
    {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      query: searchQuery || undefined,
      sortBy:
        sorting.length > 0
          ? (sorting[0].id as 'name' | 'email' | 'createdAt' | 'approved')
          : undefined,
      sortOrder:
        sorting.length > 0 ? (sorting[0].desc ? 'desc' : 'asc') : undefined,
    },
    { placeholderData: keepPreviousData },
  );

  const table = useReactTable({
    data: dataQuery.data?.records || [],
    columns,
    manualPagination: true,
    manualSorting: true,
    rowCount: dataQuery.data?.totalCount,
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
      pagination,
    },
  });

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col">
      <CountablePageHeader
        title="Vendors"
        totalCount={`${dataQuery.data?.totalCount || 0} total vendors`}
        icon={Building2}
      />

      <div className="mb-4">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          onClear={() => setSearchQuery('')}
        />
      </div>

      <StickyTableBox>
        <Table>
          <StickyTableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRowSort key={headerGroup.id} headerGroup={headerGroup} />
            ))}
          </StickyTableHeader>
          <TableBody>
            {dataQuery.isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </StickyTableBox>

      <Pagination
        currentPage={table.getState().pagination.pageIndex}
        pageSize={table.getState().pagination.pageSize}
        pageCount={table.getPageCount()}
        totalCount={table.getRowCount()}
        onPageChange={table.setPageIndex}
        onPageSizeChange={table.setPageSize}
      />
    </div>
  );
}

function VendorActionsCell({ vendor }: { vendor: VendorRecord }) {
  const [deleteOpen, setDeleteOpen] = useState(false);

  const deleteMutation = useAction('DELETE /vendor/{id}', {
    invalidate: ['GET /vendors'],
    meta: { toast: true },
    onSuccess: () => toast.success('Vendor deleted'),
  });
  const approveMutation = useAction('PATCH /vendor/{id}/approve', {
    invalidate: ['GET /vendors'],
    meta: { toast: true },
    onSuccess: () => toast.success('Vendor approved'),
  });
  const rejectMutation = useAction('PATCH /vendor/{id}/reject', {
    invalidate: ['GET /vendors'],
    meta: { toast: true },
    onSuccess: () => toast.success('Vendor rejected'),
  });
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreHorizontal className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <UpdateVendorDialog
            vendor={vendor}
            trigger={
              <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                Edit vendor
              </DropdownMenuItem>
            }
          />
          <ViewVendorDocumentsDialog vendorId={vendor.id} />
          {vendor.approved !== true && (
            <DropdownMenuItem
              disabled={approveMutation.isPending}
              onSelect={(e) => {
                e.preventDefault();
                approveMutation.mutate({ id: vendor.id });
              }}
            >
              {approveMutation.isPending ? 'Approving...' : 'Approve vendor'}
            </DropdownMenuItem>
          )}
          {vendor.approved === null && (
            <RejectApplicantDialog
              applicantId={vendor.id}
              applicantName={vendor.name}
              role="vendor"
              mutation={rejectMutation}
            />
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive"
            onSelect={(e) => {
              e.preventDefault();
              setDeleteOpen(true);
            }}
          >
            Delete vendor
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        mutation={{
          mutate: () => deleteMutation.mutate({ id: vendor.id }),
          isPending: deleteMutation.isPending,
        }}
        title="Delete vendor"
        description={
          <span>
            Are you sure you want to delete the vendor{' '}
            <strong>{vendor.name}</strong>? This action cannot be undone.
          </span>
        }
      />
    </>
  );
}
